import { FC, FormEvent, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import PageHeader from "components/PageHeader/PageHeader";
import Input from "shared/Input/Input";
import Textarea from "shared/Textarea/Textarea";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { useLocation } from "react-router-dom";
import { registerTicket } from "data/api";
import {
  BuildingOffice2Icon,
  ClockIcon,
  EnvelopeIcon,
  MapPinIcon,
  PhoneIcon,
  ShieldCheckIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";

export interface PageContactProps {
  className?: string;
}

type ContactLine = { text: string; href?: string };

type ContactChannel = {
  icon: typeof MapPinIcon;
  title: string;
  lines: ContactLine[];
};

const contactChannels: ContactChannel[] = [
  {
    icon: MapPinIcon,
    title: "Office Address",
    lines: [
      { text: "DESERT KING WATERPROOFING L.L.C" },
      { text: "Liwara 2, Ajman, United Arab Emirates" },
    ],
  },
  {
    icon: EnvelopeIcon,
    title: "Email",
    lines: [
      { text: "info@bcrltduk.com", href: "mailto:info@bcrltduk.com" },
      { text: "bcrltduk@gmail.com", href: "mailto:bcrltduk@gmail.com" },
    ],
  },
  {
    icon: PhoneIcon,
    title: "Telephone",
    lines: [
      { text: "+971 6 7421322", href: "tel:+97167421322" },
      { text: "+971 6 7421399", href: "tel:+97167421399" },
      { text: "+971 6 7456198", href: "tel:+97167456198" },
    ],
  },
  {
    icon: PhoneIcon,
    title: "Mobile",
    lines: [
      { text: "+971 506 454 351", href: "tel:+971506454351" },
      { text: "+971 506 454 328", href: "tel:+971506454328" },
      { text: "+971 506 454 326", href: "tel:+971506454326" },
    ],
  },
];

const trustItems = [
  { icon: ClockIcon, label: "Response within 24 hours" },
  { icon: TruckIcon, label: "UAE stock & fast delivery" },
  { icon: ShieldCheckIcon, label: "Technical specification support" },
  { icon: BuildingOffice2Icon, label: "Serving UAE & GCC projects" },
];

const socialLinks = [
  {
    label: "Facebook",
    href: "https://www.facebook.com/people/BCR-Ltd-Expansion-Joint-UK/100064174416066/",
    icon: "lab la-facebook-square",
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/shadow-gap-profiles/",
    icon: "lab la-linkedin-in",
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/channel/UC37h6R6_AODVk5hbxZ86-qQ",
    icon: "lab la-youtube",
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/bcr_profiles/",
    icon: "lab la-instagram",
  },
];

type LocationState = {
  name?: string;
  email?: string;
  message?: string;
  sku?: string;
};

const PageContact: FC<PageContactProps> = ({ className = "" }) => {
  const location = useLocation();
  const routeState = (location.state as LocationState | null) ?? null;

  const [user_name, setUserName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [company, setCompany] = useState("");
  const [company_email, setCompanyEmail] = useState("");
  const [company_phone, setCompanyPhone] = useState("");
  const [product, setProduct] = useState<{ sku?: string; name?: string } | null>(
    null
  );
  const [error, setError] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const productInquiry = useMemo(() => {
    if (routeState?.sku) {
      return { sku: routeState.sku, name: routeState.name || routeState.sku };
    }
    return product;
  }, [routeState, product]);

  useEffect(() => {
    if (!routeState) return;
    if (routeState.name && !routeState.sku) setUserName(routeState.name);
    if (routeState.email) setEmail(routeState.email);
    if (routeState.message) setMessage(routeState.message);
    if (routeState.sku) {
      setProduct({ sku: routeState.sku, name: routeState.name });
      if (!routeState.message) {
        setMessage(
          `Hello, I would like a quotation for:\n\nProduct: ${routeState.name || "N/A"}\nSKU: ${routeState.sku}`
        );
      }
    }
  }, [routeState]);

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    setError({});
    setSuccess("");
    setSubmitting(true);

    const payload = {
      user_name,
      phone,
      email,
      message,
      company,
      company_email,
      company_phone,
      product: productInquiry,
    };

    try {
      const val: any = await registerTicket(payload as any);
      if (val?.response && val.response.status !== 201) {
        setError(val.response.data || {});
        return;
      }
      setSuccess(
        val?.message ||
          "Thank you! Your message has been sent. Our team will contact you shortly."
      );
      setUserName("");
      setPhone("");
      setEmail("");
      setMessage("");
      setCompany("");
      setCompanyEmail("");
      setCompanyPhone("");
      setProduct(null);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "!rounded-2xl !bg-neutral-50 dark:!bg-neutral-800/70 !border-neutral-200 dark:!border-neutral-700";

  return (
    <div
      className={`nc-PageContact overflow-hidden ${className}`}
      data-nc-id="PageContact"
    >
      <Helmet>
        <title>Contact Us | BCR LTD. UK</title>
        <meta
          name="description"
          content="Contact BCR Ltd for expansion joint covers, tile trims, shadow gap profiles, LED profiles, and building solutions. Request quotes and technical support across the UAE and GCC."
        />
      </Helmet>

      <PageHeader
        title="Contact Us"
        subtitle="Have a question about our products or need a custom quote? Our team is ready to help you find the perfect building solution."
      />

      <div className="container max-w-7xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24 pt-6 sm:pt-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-10">
          {trustItems.map((item) => (
            <div
              key={item.label}
              className="flex items-center gap-3 px-4 py-3 rounded-xl border border-neutral-200/80 dark:border-neutral-700 bg-white dark:bg-neutral-900"
            >
              <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center">
                <item.icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </span>
              <span className="text-xs sm:text-sm font-medium text-neutral-700 dark:text-neutral-300 leading-snug">
                {item.label}
              </span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1.02fr_0.98fr] gap-6 lg:gap-8 items-stretch">
          {/* Info panel */}
          <div className="relative rounded-[1.75rem] overflow-hidden bg-[#09162c] text-white border border-white/10 shadow-[0_24px_70px_rgba(4,12,24,0.35)]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.16),transparent_34%),radial-gradient(circle_at_bottom_left,rgba(244,63,94,0.16),transparent_32%)]" />
            <div className="absolute inset-0 opacity-30 bcr-contact-grid" />

            <div className="relative p-6 sm:p-8 lg:p-10 flex flex-col h-full">
              <span className="inline-flex self-start items-center rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.25em] text-primary-300">
                Get in Touch
              </span>
              <h2 className="mt-4 text-2xl sm:text-3xl font-bold leading-tight">
                Speak with our BCR specialists
              </h2>
              <p className="mt-3 text-sm sm:text-base text-white/65 leading-relaxed max-w-lg">
                From expansion joint systems to tile trims and skirting profiles —
                we help you choose the right solution for your design, budget,
                and site conditions.
              </p>

              <div className="mt-8 space-y-4">
                {contactChannels.map((channel) => (
                  <div
                    key={channel.title}
                    className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-4 sm:p-5"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center shrink-0">
                        <channel.icon className="w-5 h-5 text-primary-300" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-white">
                          {channel.title}
                        </p>
                        <div className="mt-1 space-y-0.5">
                          {channel.lines.map((line) =>
                            line.href ? (
                              <a
                                key={line.text}
                                href={line.href}
                                className="block text-sm text-white/60 hover:text-white transition-colors break-all"
                              >
                                {line.text}
                              </a>
                            ) : (
                              <p
                                key={line.text}
                                className="text-sm text-white/60 leading-relaxed"
                              >
                                {line.text}
                              </p>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="text-sm font-semibold text-white mb-3">
                  Follow us
                </p>
                <div className="flex flex-wrap gap-2.5">
                  {socialLinks.map((s) => (
                    <a
                      key={s.label}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.label}
                      className="w-10 h-10 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white/80 hover:text-white transition-all flex items-center justify-center"
                    >
                      <i className={`${s.icon} text-lg`} />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <form
            className="relative rounded-[1.75rem] bg-white dark:bg-neutral-900 border border-neutral-200/70 dark:border-neutral-700/70 shadow-bcr-card p-6 sm:p-8 lg:p-10 flex flex-col"
            onSubmit={handleSubmit}
          >
            <div>
              <span className="text-xs font-semibold tracking-[0.22em] uppercase text-primary-600 dark:text-primary-400">
                Send Message
              </span>
              <h2 className="mt-2 text-2xl sm:text-[1.75rem] font-bold text-neutral-900 dark:text-white">
                Start Your Inquiry
              </h2>
              <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">
                Complete the form and our team will respond with product
                guidance, availability, or a quotation.
              </p>
            </div>

            {productInquiry?.sku && (
              <div className="mt-5 rounded-2xl border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-primary-600 dark:text-primary-400">
                  Product inquiry
                </p>
                <p className="mt-1 text-sm font-medium text-neutral-900 dark:text-white">
                  {productInquiry.name}{" "}
                  <span className="text-neutral-500">· SKU {productInquiry.sku}</span>
                </p>
              </div>
            )}

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              <label className="block">
                <span className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </span>
                <Input
                  type="text"
                  required
                  value={user_name}
                  placeholder="John Doe"
                  className={inputClass}
                  onChange={(e) => setUserName(e.target.value)}
                />
                {error.user_name && (
                  <span className="text-red-500 text-xs mt-1">{error.user_name}</span>
                )}
              </label>

              <label className="block">
                <span className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Phone <span className="text-red-500">*</span>
                </span>
                <Input
                  type="tel"
                  required
                  value={phone}
                  placeholder="+971 XXX XXX XXX"
                  className={inputClass}
                  onChange={(e) => setPhone(e.target.value)}
                />
                {error.phone && (
                  <span className="text-red-500 text-xs mt-1">{error.phone}</span>
                )}
              </label>
            </div>

            <label className="block mt-4">
              <span className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Email <span className="text-red-500">*</span>
              </span>
              <Input
                type="email"
                required
                value={email}
                placeholder="you@company.com"
                className={inputClass}
                onChange={(e) => setEmail(e.target.value)}
              />
              {error.email && (
                <span className="text-red-500 text-xs mt-1">{error.email}</span>
              )}
            </label>

            <p className="mt-6 text-xs font-semibold uppercase tracking-[0.18em] text-neutral-400">
              Company details (optional)
            </p>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              <label className="block">
                <span className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Company Name
                </span>
                <Input
                  type="text"
                  value={company}
                  placeholder="Your company"
                  className={inputClass}
                  onChange={(e) => setCompany(e.target.value)}
                />
              </label>

              <label className="block">
                <span className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Company Phone
                </span>
                <Input
                  type="tel"
                  value={company_phone}
                  placeholder="+971 XXX XXX XXX"
                  className={inputClass}
                  onChange={(e) => setCompanyPhone(e.target.value)}
                />
              </label>
            </div>

            <label className="block mt-4">
              <span className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Company Email
              </span>
              <Input
                type="email"
                value={company_email}
                placeholder="company@example.com"
                className={inputClass}
                onChange={(e) => setCompanyEmail(e.target.value)}
              />
            </label>

            <label className="block mt-4 flex-1">
              <span className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Message <span className="text-red-500">*</span>
              </span>
              <Textarea
                required
                rows={5}
                value={message}
                placeholder="Tell us about your project, product requirement, BOQ, or quotation request..."
                className={`${inputClass} !rounded-[1.25rem] px-4 py-3`}
                onChange={(e) => setMessage(e.target.value)}
              />
              {error.message && (
                <span className="text-red-500 text-xs mt-1">{error.message}</span>
              )}
            </label>

            {success && (
              <div className="mt-4 rounded-2xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 px-4 py-3 text-sm text-green-700 dark:text-green-300">
                {success}
              </div>
            )}

            <div className="mt-6">
              <ButtonPrimary
                type="submit"
                className="w-full !py-3.5 !rounded-full"
                disabled={submitting}
              >
                {submitting ? "Sending..." : "Send Message"}
              </ButtonPrimary>
            </div>

            <p className="mt-4 text-xs text-neutral-400 dark:text-neutral-500 text-center">
              Prefer a quick call?{" "}
              <a
                href="tel:+971506454326"
                className="text-primary-600 dark:text-primary-400 font-medium hover:underline"
              >
                +971 506 454 326
              </a>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PageContact;
